import React from 'react';
import {
  Container,
  Box,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Alert,
  CircularProgress
} from '@mui/material';
import TrophyIcon from '@mui/icons-material/EmojiEvents';
import PeopleIcon from '@mui/icons-material/People';
import { useGetLeaderboardQuery } from '../../Apis/progressApi';





export default function ParticipantsTable() {
const { data: leaderboardData, isLoading, error } = useGetLeaderboardQuery();

  if (isLoading) {
  return (
    <Container 
      maxWidth={false} 
      sx={{ 
        minHeight: '100vh', 
        backgroundColor: 'grey.50',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <CircularProgress color="primary" />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading leaderboard...
        </Typography>
      </Box>
    </Container>
  );
}

// Error state
if (error) {
  return (
    <Container 
      maxWidth={false} 
      sx={{ 
        minHeight: '100vh', 
        backgroundColor: 'grey.50',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}
    >
      <Alert severity="error" sx={{ textAlign: 'center' }}>
        Failed to load leaderboard data.
      </Alert>
    </Container>
  );
}

  // Access the nested properties from the response object
  const topTen = leaderboardData?.topTen || [];
  const allParticipants = leaderboardData?.allParticipants || [];  
  const totalParticipants = leaderboardData?.totalParticipants || 0;
  

  const getRankBadge = (rank: number): string => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `#${rank}`;
  };

  const getScoreColor = (score: number): string => {
    if (score >= 90) return 'text-success';
    if (score >= 70) return 'text-warning';
    return 'text-danger';
  };


  return (
    <Container maxWidth="lg" sx={{ py: 1, minHeight: '100vh', px: { xs: 0, md: 0 } }}>
    {/* Header */}
    <Box sx={{ p: 1 }}>
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="h4" sx={{ color: "rgba(81, 35, 132, 1)", fontWeight: 600 }}>
            Contest Scoreboard
          </Typography>
        </Box>
      </Container>
    </Box>

    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box>
        <Card sx={{ borderRadius: 3, boxShadow: 4, mb: 4 }}>
          <CardHeader
            title={
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <TrophyIcon sx={{ mr: 1, color: "#FFD700" }} />
                <Typography variant="h6" sx={{ color: "white", fontWeight: "bold" }}>
                  Top 10 Leaderboard
                </Typography>
              </Box>
            }
            sx={{ backgroundColor: "primary.main" }}
          />
          <CardContent sx={{ p: 0, overflow: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell sx={{ fontWeight: "bold" }}>Rank#</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Full Name</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Entity</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Total Scores</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Quiz Taken</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {topTen.map((participant) => (
                  <TableRow
                    key={participant.userId}
                    hover
                    sx={{
                      "&:hover": {
                        backgroundColor: "rgba(0, 0, 0, 0.04)",
                      },
                    }}
                  >
                    <TableCell>
                      <Typography fontWeight="bold">
                        {getRankBadge(participant.rank)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight="bold">
                        {participant.fullName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={participant.entity} 
                        color="secondary" 
                        size="small" 
                      />
                    </TableCell>
                    <TableCell>
                      <Typography 
                        fontWeight="bold" 
                        sx={{ color: getScoreColor(participant.totalScore) }}
                      >
                        {participant.totalScore}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={participant.quizzesTaken}
                        color="info"
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* All Participants Table */}
        <Card sx={{ borderRadius: 3, boxShadow: 4 }}>
          <CardHeader
            title={
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <PeopleIcon sx={{ mr: 1, color: "white" }} />
                <Typography variant="h6" sx={{ color: "white", fontWeight: "bold" }}>
                  All Participants ({totalParticipants})
                </Typography>
              </Box>
            }
            sx={{ backgroundColor: "grey.600" }}
          />
          <CardContent sx={{ p: 0, overflow: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell sx={{ fontWeight: "bold" }}>Rank#</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Full Name</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Entity</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Total Scores</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Quiz Taken</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allParticipants.map((participant) => (
                  <TableRow
                    key={participant.userId}
                    hover
                    sx={{
                      "&:hover": {
                        backgroundColor: "rgba(0, 0, 0, 0.04)",
                      },
                    }}
                  >
                    <TableCell>
                      <Typography fontWeight="bold">
                        {participant.rank}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography>
                        {participant.fullName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography>
                        {participant.entity}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography 
                        sx={{ color: getScoreColor(participant.totalScore) }}
                      >
                        {participant.totalScore}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={participant.quizzesTaken}
                        color="info"
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </Box>
    </Container>
  </Container>
  );
};
